import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "./http";

// A tiny query cache for reads (results, node detail, home, search…). Keys include the worldview
// revision, so a write (or an SSE revision event) re-keys every read and they refetch. While a new
// key is loading, the hook keeps the previous data on screen (the old `useAsyncResult` behaviour).

interface Entry {
	data?: unknown;
	promise?: Promise<unknown>;
}

const cache = new Map<string, Entry>();
const MAX = 300;

function touch(key: string, e: Entry) {
	cache.delete(key);
	cache.set(key, e);
	if (cache.size > MAX) cache.delete(cache.keys().next().value!);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Fetch once per key (in-flight requests are shared); retries an engine that is busy. */
export function fetchQuery<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
	const hit = cache.get(key);
	if (hit && "data" in hit) return Promise.resolve(hit.data as T);
	if (hit?.promise) return hit.promise as Promise<T>;
	const run = async (): Promise<T> => {
		for (let attempt = 0; ; attempt++) {
			try {
				return await fetcher();
			} catch (e) {
				// 503 ENGINE_BUSY: the run carries on on the server and fills its cache, so the retry is quick.
				if (e instanceof ApiError && e.status === 503 && attempt < 4) {
					await sleep((e.retryAfterS ?? 2) * 1000);
					continue;
				}
				throw e;
			}
		}
	};
	const promise = run().then(
		(data) => {
			touch(key, { data });
			return data;
		},
		(err) => {
			cache.delete(key);
			throw err;
		},
	);
	touch(key, { promise });
	return promise;
}

export function peekQuery<T>(key: string): T | undefined {
	const e = cache.get(key);
	return e && "data" in e ? (e.data as T) : undefined;
}

/** Seed a key with data the app already has (e.g. the series a forecast save returned). */
export function setQueryData<T>(key: string, data: T) {
	touch(key, { data });
}

export function clearQueries() {
	cache.clear();
}

const reported = new Set<string>();

/**
 * Read `key` with `fetcher`. `null` skips the read. Returns the latest data for this key, or the
 * previous key's data while this one loads (`pending`).
 */
export function useQuery<T>(key: string | null, fetcher: () => Promise<T>): { data: T | null; pending: boolean; error: ApiError | null } {
	const [state, setState] = useState<{ key: string; data: T } | null>(null);
	const [error, setError] = useState<{ key: string; error: ApiError } | null>(null);
	const cached = key !== null ? peekQuery<T>(key) : undefined;

	useEffect(() => {
		if (key === null || peekQuery(key) !== undefined) return;
		let alive = true;
		fetchQuery(key, fetcher).then(
			(data) => {
				if (alive) setState({ key, data });
			},
			(e: unknown) => {
				if (!alive) return;
				const err = e instanceof ApiError ? e : new ApiError(0, "CLIENT", (e as Error)?.message ?? "Something went wrong");
				setError({ key, error: err });
				// One toast per failing read (not for the ones a signed-out session makes).
				if (err.status !== 401 && err.status !== 404 && !reported.has(key)) {
					reported.add(key);
					toast.error(err.messages.join(" "));
				}
			},
		);
		return () => {
			alive = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);

	if (key === null) return { data: null, pending: false, error: null };
	if (cached !== undefined) return { data: cached, pending: false, error: null };
	const failed = error?.key === key ? error.error : null;
	return { data: state?.data ?? null, pending: !failed, error: failed };
}
